import axios from 'axios'
import router from '@/router'

function buildBaseURL(subdomain) {
  const isDev = import.meta.env.DEV
  const protocol = isDev ? 'http' : 'https'
  const port = isDev ? ':8000' : ''
  return `${protocol}://${subdomain}${port}`
}

function isCurrentRoutePublic() {
  return router.currentRoute.value?.matched.some((r) => r.meta.allowUnauthenticated)
}

function redirectToLogin(fullPath = router.currentRoute.value.fullPath) {
  if (isCurrentRoutePublic()) return
  router.replace({ name: 'login', query: { redirect: fullPath } })
}

const initialDomain = localStorage.getItem('domain')

export const axiosInstance = axios.create({
  baseURL: initialDomain ? buildBaseURL(initialDomain) : undefined,
  headers: { 'Content-Type': 'application/json' }
})

export const authAxios = axios.create({
  baseURL: import.meta.env.VITE_APP_BASE_URL,
  headers: { 'Content-Type': 'application/json' }
})

export function setTenantDomain(subdomain) {
  const url = buildBaseURL(subdomain)
  axiosInstance.defaults.baseURL = url
  localStorage.setItem('domain', subdomain)
}

axiosInstance.interceptors.request.use((config) => {
  if (!axiosInstance.defaults.baseURL) {
    const storedDomain = localStorage.getItem('domain')
    if (storedDomain) {
      setTenantDomain(storedDomain)
    } else {
      // If the tenant domain isn’t available, reject early.
      return Promise.reject(new Error('Tenant domain not set'))
    }
  }
  return config
})

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isCurrentRoutePublic()) return Promise.reject(error)
    const originalRequest = error.config
    if (!originalRequest || !error.response) {
      return Promise.reject(error)
    }

    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      const refreshToken = localStorage.getItem('refresh')
      if (refreshToken) {
        try {
          const response = await authAxios.post('/auth/jwt/refresh/', {
            refresh: refreshToken
          })
          const newAccessToken = response.data.access
          localStorage.setItem('access', newAccessToken)
          axiosInstance.defaults.headers.common['Authorization'] = `JWT ${newAccessToken}`
          originalRequest.headers['Authorization'] = `JWT ${newAccessToken}`
          return axiosInstance(originalRequest)
        } catch (refreshError) {
          localStorage.removeItem('access')
          localStorage.removeItem('refresh')
          redirectToLogin()
          return Promise.reject(refreshError)
        }
      } else {
        localStorage.removeItem('access')
        localStorage.removeItem('refresh')
        redirectToLogin()
        return Promise.reject(error)
      }
    }
    if (error.response.status === 401) {
      redirectToLogin()
    }
    return Promise.reject(error)
  }
)

export default axiosInstance
